import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { useProducts } from '../hooks/useProducts';
import { fetchStores } from '../api/stores';
import { getPosStoreId, getPosToken } from './POSLoginPage';
import POSMenu from '../components/POSMenu';
import { createOrder } from '../api/orders';
import { fetchProductComments } from '../api/products';
import { lookupCustomerByPhone } from '../api/customers';
import { getCompany } from '../api/company';
import { getErrorMessage } from '../api/client';
import { API_BASE_URL } from '../config';
import { stockSearchScore, stockSummary } from '../utils/stockAvailability';
import { buildA4SalesDocumentHtml, openPrintableDocument } from '../utils/salesDocumentPrint';

// Icons as inline SVG for dark theme
const IconSearch = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);
const IconBarcode = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m-4-8v8m8-8v8m-4-16v16m-4-8v8" />
  </svg>
);
const IconHashtag = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
  </svg>
);
const IconTag = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);
const IconTrash = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const IconCart = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);
const IconPlus = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);
const IconPercent = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);
const IconComment = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);
const IconUser = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);
const IconSave = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
  </svg>
);
const IconRefund = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);
const IconLock = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);
const IconTransfer = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
);
const IconClose = () => (
  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.4} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const IconBasket = () => (
  <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7h18m-2 0l-1.1 11.2A2 2 0 0115.9 20H8.1a2 2 0 01-1.99-1.8L5 7m4 0V5a3 3 0 016 0v2m-6 4v5m6-5v5" />
  </svg>
);
const IconEllipsis = () => (
  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

const formatPeso = (value, withSymbol = false) =>
  `${withSymbol ? 'P' : ''}${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

function normalizedCommentEntries(item) {
  return (Array.isArray(item?.commentEntries) ? item.commentEntries : [])
    .map((entry) => ({
      text: String(entry?.text || '').trim(),
      fromProduct: entry?.fromProduct === true,
    }))
    .filter((entry) => entry.text);
}

function cartItemToOrderItem(item) {
  const commentEntries = normalizedCommentEntries(item);
  return {
    productId: item.id,
    name: item.name,
    sku: item.sku || '',
    unitOfMeasurement: item.unitOfMeasurement || 'pc',
    price: item.price,
    qty: item.qty,
    discountPercent: Math.min(100, Math.max(0, Number(item.discountPercent) || 0)),
    discountAmount: Math.max(0, Number(item.discountFixed) || 0),
    image: item.image || '',
    comments: commentEntries.map((entry) => entry.text),
    consumedComments: commentEntries
      .filter((entry) => entry.fromProduct)
      .map((entry) => entry.text),
  };
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export default function POSPage({ onLogout }) {
  const cart = useCart();
  const searchInputRef = useRef(null);
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(() => getPosStoreId() || '');
  const posStoreIdForCatalog =
    selectedStoreId && /^[a-fA-F0-9]{24}$/.test(selectedStoreId) ? selectedStoreId : '';
  const { products, isLoading: productsLoading, isRefreshing: productsRefreshing, refetch: refetchProducts } =
    useProducts(API_BASE_URL, { storeId: posStoreIdForCatalog, fields: 'pos' });
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentOptionsLoading, setCommentOptionsLoading] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentError, setCommentError] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [customerName, setCustomerName] = useState('Walk-in');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [orderNote, setOrderNote] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountFixed, setDiscountFixed] = useState(0);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountType, setDiscountType] = useState('percent'); // 'percent' | 'fixed'
  const [discountTarget, setDiscountTarget] = useState('cart'); // 'cart' | 'item'
  const [isCompleting, setIsCompleting] = useState(false);
  const [completeError, setCompleteError] = useState('');
  const [lastOrder, setLastOrder] = useState(null);
  const [cashReceived, setCashReceived] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showCashInOut, setShowCashInOut] = useState(false);
  const [showEndOfDay, setShowEndOfDay] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);

  useEffect(() => {
    fetchStores()
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setStores(arr);
        setSelectedStoreId((prev) => prev || (arr[0]?._id || arr[0]?.id || ''));
      })
      .catch(() => setStores([]));
  }, []);

  const posStoreName = useMemo(
    () => stores.find((s) => String(s._id || s.id) === String(selectedStoreId))?.name || '',
    [stores, selectedStoreId]
  );

  const filteredProducts = useMemo(() => {
    const term = (search || '').trim().toLowerCase();
    let list = !term
      ? products.slice(0, 50)
      : products
          .filter(
            (p) =>
              (p.name || '').toLowerCase().includes(term) ||
              (p.sku || '').toLowerCase().includes(term) ||
              (p.barcode || '').toLowerCase().includes(term)
          )
          .slice(0, 30);
    if (term) {
      list = [...list].sort((a, b) => stockSearchScore(b) - stockSearchScore(a));
    }
    return list;
  }, [products, search]);

  const loadProductComments = useCallback(async (productId) => {
    if (!productId) return [];
    setCommentOptionsLoading(true);
    setCommentError('');
    try {
      const comments = await fetchProductComments(productId, {
        ...(posStoreIdForCatalog ? { storeId: posStoreIdForCatalog } : {})
      }, {
        headers: { Authorization: `Bearer ${getPosToken()}` }
      });
      cart.updateItemProductComments(productId, comments);
      return comments;
    } catch (err) {
      setCommentError(getErrorMessage(err, 'Failed to load product comments.'));
      return [];
    } finally {
      setCommentOptionsLoading(false);
    }
  }, [cart, posStoreIdForCatalog]);

  const addToCart = useCallback(
    async (product) => {
      setCompleteError('');
      setLastOrder(null);
      cart.addItem({ ...product, id: product._id });
      setSelectedItemId(product._id);
      setSearch('');
      setSearchFocused(false);
      setShowSearchModal(false);
      if (Number(product.posCommentCount || 0) > 0) {
        setCommentDraft('');
        setCommentError('');
        setShowCommentModal(true);
        await loadProductComments(product._id);
      }
    },
    [cart, loadProductComments]
  );

  const addByCodeOrBarcode = useCallback(() => {
    const term = (search || '').trim();
    if (!term) return;
    const byBarcode = products.find((p) => (p.barcode || '').toString() === term);
    const byCode = products.find((p) => (p.sku || '').toString().toLowerCase() === term.toLowerCase());
    const match = byBarcode || byCode;
    if (match) {
      addToCart(match);
      setSearch('');
    }
  }, [search, products, addToCart]);

  const itemSubtotals = useMemo(
    () => cart.items.map((i) => {
      const raw = (i.price || 0) * (i.qty || 0);
      const itemDisc = Math.min(raw, (i.discountPercent ? raw * (i.discountPercent / 100) : 0) + (i.discountFixed || 0));
      return { raw, itemDisc, net: Math.max(0, raw - itemDisc) };
    }),
    [cart.items]
  );
  const subtotalBeforeCartDiscount = useMemo(() => itemSubtotals.reduce((s, x) => s + x.net, 0), [itemSubtotals]);
  const discountAmount = useMemo(() => {
    if (discountType === 'percent') return subtotalBeforeCartDiscount * (discountPercent / 100);
    return discountFixed || 0;
  }, [discountType, discountPercent, discountFixed, subtotalBeforeCartDiscount]);
  const taxAmount = 0;
  const total = useMemo(() => subtotalBeforeCartDiscount - discountAmount + taxAmount, [subtotalBeforeCartDiscount, discountAmount, taxAmount]);

  const beginPayment = useCallback((method = paymentMethod) => {
    if (cart.items.length === 0) {
      setCompleteError('Cart is empty.');
      return;
    }
    setPaymentMethod(method);
    setPaymentReference('');
    setPaymentError('');
    setCashReceived(Number(total).toFixed(2));
    setShowPaymentModal(true);
  }, [cart.items.length, paymentMethod, total]);

  const removeItem = useCallback(
    (id) => {
      const item = cart.items.find((i) => i.id === id);
      if (item) cart.updateQty(id, 0);
      setSelectedItemId(null);
      setShowQuantityModal(false);
      setShowCommentModal(false);
    },
    [cart]
  );

  const handleLookupPhone = useCallback(async () => {
    if (!customerPhone.trim()) return;
    try {
      const c = await lookupCustomerByPhone(customerPhone.trim());
      if (c) {
        setCustomerName(c.name || '');
        setCustomerAddress(c.address || '');
        setCustomerEmail(c.email || '');
      }
    } catch (_) {}
  }, [customerPhone]);

  const handleNewSale = useCallback(() => {
    if (cart.items.length > 0 && !window.confirm('Start new sale? Current items will be cleared.')) return;
    cart.clearCart();
    setLastOrder(null);
    setCompleteError('');
    setDiscountPercent(0);
    setDiscountFixed(0);
    setOrderNote('');
    setPaymentReference('');
    setPaymentError('');
    setCashReceived('');
    setSelectedItemId(null);
    setShowCommentModal(false);
    setShowPaymentModal(false);
    setCommentDraft('');
    setCommentError('');
  }, [cart]);

  const clearDiscounts = useCallback(() => {
    setDiscountPercent(0);
    setDiscountFixed(0);
    cart.items.forEach((i) => cart.updateItemDiscount(i.id, 0, 0));
  }, [cart]);

  const handlePayment = useCallback(async () => {
    if (cart.items.length === 0) {
      setCompleteError('Cart is empty.');
      return;
    }
    const method = paymentMethod;
    const reference = paymentReference.trim();
    const tendered = method === 'cash' ? Number(cashReceived) || 0 : Number(total);
    if (method === 'cash' && tendered < total) {
      setPaymentError('Cash received must cover the total.');
      return;
    }
    if (method !== 'cash' && !reference) {
      setPaymentError('Enter the transaction reference number.');
      return;
    }
    setPaymentError('');
    setCompleteError('');
    setIsCompleting(true);
    try {
      const saleItems = [...cart.items];
      const payload = {
        source: 'pos',
        storeId: selectedStoreId || undefined,
        customerName: (customerName || '').trim() || 'Walk-in',
        customerPhone: (customerPhone || '').trim() || '-',
        customerEmail: (customerEmail || '').trim(),
        customerAddress: (customerAddress || '').trim() || '-',
        notes: orderNote || undefined,
        paymentMethod: method,
        paymentReference: reference,
        paidAmount: tendered,
        items: cart.items.map(cartItemToOrderItem),
        discountAmount,
        taxAmount,
        status: 'completed'
      };
      const res = await createOrder(payload, {
        headers: { Authorization: `Bearer ${getPosToken()}` }
      });
      setLastOrder({
        orderId: res.orderId,
        documentNumber: res.documentNumber,
        date: new Date().toISOString(),
        createdBy: (typeof window !== 'undefined' && sessionStorage?.getItem?.('pos_username')) || 'Admin',
        customerName: (customerName || '').trim() || 'Walk-in',
        customerPhone: (customerPhone || '').trim() || '-',
        customerEmail: (customerEmail || '').trim(),
        customerAddress: (customerAddress || '').trim() || '-',
        paymentMethod: method,
        paymentStatus: res.payment?.status || 'paid',
        paymentReference: res.payment?.reference || reference,
        paidAmount: Number(res.payment?.paidAmount ?? tendered),
        changeAmount: Number(res.payment?.changeAmount ?? Math.max(0, tendered - total)),
        subtotalAmount: subtotalBeforeCartDiscount,
        discountAmount,
        taxAmount,
        totalAmount: total,
        total,
        items: saleItems,
      });
      setShowPaymentModal(false);
      cart.clearCart();
      setSelectedItemId(null);
      setShowCommentModal(false);
      setDiscountPercent(0);
      setOrderNote('');
      refetchProducts({ clearCache: true });
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to complete sale.');
      setCompleteError(message);
      if (err?.response?.status === 409) {
        setCommentError(message);
        setShowCommentModal(true);
        refetchProducts({ clearCache: true });
        loadProductComments(selectedItemId || cart.items[0]?.id)
          .then(() => setCommentError(message));
      }
    } finally {
      setIsCompleting(false);
    }
  }, [cart, selectedStoreId, customerName, customerPhone, customerEmail, customerAddress, orderNote, paymentMethod, paymentReference, cashReceived, total, subtotalBeforeCartDiscount, discountAmount, taxAmount, refetchProducts, loadProductComments, selectedItemId]);

  const handleSaveSale = useCallback(async () => {
    if (cart.items.length === 0) {
      setCompleteError('Cart is empty.');
      return;
    }
    setCompleteError('');
    setIsCompleting(true);
    try {
      const payload = {
        source: 'pos',
        storeId: selectedStoreId || undefined,
        customerName: (customerName || '').trim() || 'Walk-in',
        customerPhone: (customerPhone || '').trim() || '-',
        customerEmail: (customerEmail || '').trim(),
        customerAddress: (customerAddress || '').trim() || '-',
        notes: orderNote || 'Saved - pending payment' || undefined,
        paymentMethod: 'cod',
        items: cart.items.map(cartItemToOrderItem),
        discountAmount,
        taxAmount,
        status: 'pending'
      };
      await createOrder(payload, {
        headers: { Authorization: `Bearer ${getPosToken()}` }
      });
      cart.clearCart();
      setSelectedItemId(null);
      setShowCommentModal(false);
      setDiscountPercent(0);
      setOrderNote('');
      setCompleteError('');
      refetchProducts({ clearCache: true });
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to save sale.');
      setCompleteError(message);
      if (err?.response?.status === 409) {
        setCommentError(message);
        setShowCommentModal(true);
        refetchProducts({ clearCache: true });
        loadProductComments(selectedItemId || cart.items[0]?.id)
          .then(() => setCommentError(message));
      }
    } finally {
      setIsCompleting(false);
    }
  }, [cart, selectedStoreId, customerName, customerPhone, customerEmail, customerAddress, orderNote, discountAmount, taxAmount, refetchProducts, loadProductComments, selectedItemId]);

  const handlePrintReceipt = useCallback(async () => {
    if (!lastOrder) return;
    let company = {};
    try {
      company = await getCompany() || {};
    } catch (_) {}
    const rec = lastOrder.documentNumber || lastOrder.orderId || `REC-${Date.now()}`;
    const dt = new Date();
    const dateTime = `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()} ${String(dt.getHours() % 12 || 12).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}:${String(dt.getSeconds()).padStart(2, '0')} ${dt.getHours() >= 12 ? 'pm' : 'am'}`;
    const userName = lastOrder.createdBy || 'Admin';
    const cust = lastOrder.customerName || 'Walk-in';
    const custAddr = lastOrder.customerAddress || '-';
    const custPhone = lastOrder.customerPhone || '-';
    const items = lastOrder.items || [];
    const subtotal = Number(lastOrder.subtotalAmount) || 0;
    const discount = Number(lastOrder.discountAmount) || 0;
    const tax = Number(lastOrder.taxAmount) || 0;
    const saleTotal = Number(lastOrder.totalAmount ?? lastOrder.total) || 0;
    const paid = Number(lastOrder.paidAmount) || 0;
    const change = Number(lastOrder.changeAmount) || 0;
    const c = company;
    const barcodeUrl = `https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(rec)}&code=Code128`;
    const html = `
<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt ${rec}</title>
<style>body{font-family:Arial,sans-serif;font-size:12px;max-width:320px;margin:0 auto;padding:12px;color:#000}
.logo{max-height:48px;margin-bottom:8px}.company{font-weight:bold;margin-bottom:4px}
table{width:100%;border-collapse:collapse;margin:8px 0}td{padding:2px 0}
.total{font-size:16px;font-weight:bold;margin-top:8px}
.barcode{width:100%;height:48px;object-fit:contain;margin:8px 0}
</style></head><body>
${c.logo ? `<img src="${c.logo}" alt="Logo" class="logo"/>` : ''}
<div class="company">${c.name || 'Company'}</div>
<div>${c.address || '-'}</div>
<div>Tax No.: ${c.taxId || '-'}</div>
<div>${c.phone || '-'}</div>
<div>${c.email || '-'}</div>
<hr/>
<div><strong>Receipt No.:</strong> ${rec}</div>
<div><strong>Date & Time:</strong> ${dateTime}</div>
<div><strong>User:</strong> ${userName}</div>
<hr/>
<div><strong>Customer:</strong> ${cust}</div>
<div>${custAddr}</div>
<div>Phone: ${escapeHtml(custPhone)}</div>
${lastOrder.customerEmail ? `<div>Email: ${escapeHtml(lastOrder.customerEmail)}</div>` : ''}
<hr/>
<table>
${items.map((i) => {
  const q = i.qty || 0;
  const p = i.price || 0;
  const t = q * p;
  const comments = normalizedCommentEntries(i);
  const commentRows = comments
    .map((entry) => {
      const label = entry.fromProduct ? 'SN' : 'Comment';
      return `<tr><td colspan="2" style="padding-left:10px;font-size:11px"><strong>${label}:</strong> ${escapeHtml(entry.text)}</td></tr>`;
    })
    .join('');
  return `<tr><td>${escapeHtml(i.name)}</td></tr><tr><td>${q} pcs x P${Number(p).toFixed(2)}</td><td style="text-align:right">P${Number(t).toFixed(2)}</td></tr>${commentRows}`;
}).join('')}
</table>
<hr/>
<div>Items count: ${items.length}</div>
<div>Subtotal: P${Number(subtotal).toFixed(2)}</div>
<div>Discount: P${Number(discount).toFixed(2)}</div>
<div>Tax: P${Number(tax).toFixed(2)}</div>
<div class="total">TOTAL: P${Number(saleTotal).toFixed(2)}</div>
<hr/>
<div><strong>Payment:</strong> ${escapeHtml(String(lastOrder.paymentMethod || 'cash').replace('_', ' '))}</div>
${lastOrder.paymentReference ? `<div><strong>Reference:</strong> ${escapeHtml(lastOrder.paymentReference)}</div>` : ''}
<div>Paid amount: P${Number(paid).toFixed(2)}</div>
<div>Change: P${Number(change).toFixed(2)}</div>
<hr/>
<img src="${barcodeUrl}" alt="Barcode" class="barcode" onerror="this.style.display='none'"/>
<div style="text-align:center;font-size:10px">Receipt No.: ${rec}</div>
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.print();
    w.close();
  }, [lastOrder]);

  const openA4Invoice = useCallback(async () => {
    if (!lastOrder) return;
    let company = {};
    try {
      company = await getCompany() || {};
    } catch (_) {}
    openPrintableDocument(buildA4SalesDocumentHtml({ company, sale: lastOrder, documentType: 'invoice' }));
  }, [lastOrder]);

  const handleSendEmail = useCallback(() => {
    if (!lastOrder) return;
    const recipient = lastOrder.customerEmail || window.prompt('Customer email address:') || '';
    if (!recipient.trim()) return;
    const number = lastOrder.documentNumber || lastOrder.orderId;
    const lines = (lastOrder.items || []).map((item) => {
      const quantity = Number(item.qty) || 0;
      return `${item.name} - ${quantity} x P${Number(item.price || 0).toFixed(2)}`;
    });
    const subject = encodeURIComponent(`Invoice ${number} - iConnectStore`);
    const body = encodeURIComponent([
      `Invoice: ${number}`,
      `Customer: ${lastOrder.customerName || 'Walk-in'}`,
      '',
      ...lines,
      '',
      `Total: P${Number(lastOrder.totalAmount || 0).toFixed(2)}`,
      `Payment: ${String(lastOrder.paymentMethod || '').replace('_', ' ')}`,
      lastOrder.paymentReference ? `Reference: ${lastOrder.paymentReference}` : '',
      '',
      'Thank you for your purchase.'
    ].filter(Boolean).join('\n'));
    window.location.href = `mailto:${encodeURIComponent(recipient.trim())}?subject=${subject}&body=${body}`;
  }, [lastOrder]);

  const openQuantityModal = useCallback((id) => {
    setSelectedItemId(id);
    setShowQuantityModal(true);
  }, []);

  const [searchMode, setSearchMode] = useState('name'); // 'barcode' | 'code' | 'name'

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Delete' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        if (selectedItemId) {
          removeItem(selectedItemId);
          setSelectedItemId(null);
        }
        return;
      }
      if (e.ctrlKey && e.key.toLowerCase() === 't') {
        e.preventDefault();
        setSearchMode((m) => (m === 'barcode' ? 'code' : m === 'code' ? 'name' : 'barcode'));
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      switch (e.key) {
        case 'F2': e.preventDefault(); setShowDiscountModal(true); break;
        case 'F3': e.preventDefault(); setSearchFocused(true); searchInputRef.current?.focus(); break;
        case 'F4': e.preventDefault(); if (selectedItemId || cart.items[0]) openQuantityModal(selectedItemId || cart.items[0]?.id); break;
        case 'F8': e.preventDefault(); handleNewSale(); break;
        case 'F9': e.preventDefault(); handleSaveSale(); break;
        case 'F10': e.preventDefault(); beginPayment(); break;
        case 'F12': e.preventDefault(); beginPayment('cash'); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleNewSale, handleSaveSale, beginPayment, openQuantityModal, removeItem, selectedItemId, cart.items]);

  const selectedItem = useMemo(() => cart.items.find((i) => i.id === selectedItemId), [cart.items, selectedItemId]);
  const selectedCommentEntries = useMemo(
    () => normalizedCommentEntries(selectedItem),
    [selectedItem]
  );
  const selectedProduct = useMemo(
    () => products.find((product) => String(product._id) === String(selectedItem?.id)),
    [products, selectedItem?.id]
  );
  const productCommentPool = useMemo(() => {
    const values = Array.isArray(selectedProduct?.posComments)
      ? selectedProduct.posComments
      : (Array.isArray(selectedItem?.posComments) ? selectedItem.posComments : []);
    return [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))];
  }, [selectedProduct, selectedItem]);
  const availableProductComments = useMemo(() => {
    const selected = new Set(selectedCommentEntries.map((entry) => entry.text));
    return productCommentPool.filter((comment) => !selected.has(comment));
  }, [productCommentPool, selectedCommentEntries]);

  const openItemCommentModal = useCallback(async () => {
    const itemId = selectedItemId || cart.items[0]?.id;
    if (!itemId) {
      setCompleteError('Select or add a product first.');
      return;
    }
    setSelectedItemId(itemId);
    setCommentDraft('');
    setCommentError('');
    setShowCommentModal(true);
    await loadProductComments(itemId);
  }, [cart.items, loadProductComments, selectedItemId]);

  const addProductComment = useCallback((comment) => {
    if (!selectedItem) return;
    const entries = normalizedCommentEntries(selectedItem);
    const serialCount = entries.filter((entry) => entry.fromProduct).length;
    if (serialCount >= Number(selectedItem.qty || 1)) {
      setCommentError(`Quantity is ${selectedItem.qty || 1}. Increase quantity before selecting another serial/comment.`);
      return;
    }
    if (entries.some((entry) => entry.text === comment)) return;
    cart.updateItemComments(selectedItem.id, [...entries, { text: comment, fromProduct: true }]);
    setCommentError('');
  }, [cart, selectedItem]);

  const addCustomComment = useCallback(() => {
    if (!selectedItem) return;
    const text = commentDraft.trim();
    if (!text) return;
    if (productCommentPool.includes(text)) {
      addProductComment(text);
      setCommentDraft('');
      return;
    }
    const entries = normalizedCommentEntries(selectedItem);
    if (!entries.some((entry) => entry.text === text)) {
      cart.updateItemComments(selectedItem.id, [
        ...entries,
        { text, fromProduct: false },
      ]);
    }
    setCommentDraft('');
    setCommentError('');
  }, [addProductComment, cart, commentDraft, productCommentPool, selectedItem]);

  const removeItemComment = useCallback((index) => {
    if (!selectedItem) return;
    const entries = normalizedCommentEntries(selectedItem);
    cart.updateItemComments(selectedItem.id, entries.filter((_, entryIndex) => entryIndex !== index));
    setCommentError('');
  }, [cart, selectedItem]);

  const updateSelectedItemQuantity = useCallback((nextQuantity) => {
    if (!selectedItem) return;
    const minimum = normalizedCommentEntries(selectedItem)
      .filter((entry) => entry.fromProduct).length;
    if (nextQuantity > 0 && nextQuantity < minimum) {
      setCompleteError(`Remove a selected serial/comment before reducing quantity below ${minimum}.`);
      return;
    }
    cart.updateQty(selectedItem.id, nextQuantity);
  }, [cart, selectedItem]);

  return (
    <div className="h-[100dvh] bg-[#242424] text-white flex flex-col overflow-hidden font-sans relative">
      <header className="relative z-40 shrink-0 bg-[#303030] border-b border-[#1696d2] flex flex-wrap items-center gap-2 px-2 py-2 xl:min-h-[74px] xl:flex-nowrap xl:py-0">
        <div className="order-2 flex h-8 w-full items-center justify-between border-b border-[#1696d2] px-2 text-gray-300 xl:order-none xl:h-full xl:w-auto xl:justify-start xl:gap-5 xl:border-b-0 xl:px-0">
          <Link to="/" className="text-2xl leading-none px-2 hover:text-white xl:text-3xl xl:px-3" title="Store">*</Link>
          <button type="button" onClick={() => setSearchMode('barcode')} className={`${searchMode === 'barcode' ? 'text-[#25aeea]' : 'hover:text-white'}`} title="Barcode">
            <IconBarcode />
          </button>
          <button type="button" onClick={() => setSearchMode('code')} className={`${searchMode === 'code' ? 'text-[#25aeea]' : 'hover:text-white'}`} title="Code">
            <IconHashtag />
          </button>
          <button type="button" onClick={() => setSearchMode('name')} className={`${searchMode === 'name' ? 'text-[#25aeea]' : 'hover:text-white'}`} title="Name">
            <IconTag />
          </button>
          <div className="xl:hidden">
            {onLogout && (
              <POSMenu
                open={menuOpen}
                onToggle={() => setMenuOpen((o) => !o)}
                onLogout={() => { setMenuOpen(false); onLogout(); }}
                onCashInOut={() => { setMenuOpen(false); setShowCashInOut(true); }}
                onEndOfDay={() => { setMenuOpen(false); setShowEndOfDay(true); }}
                onUserInfo={() => { setMenuOpen(false); setShowUserInfo(true); }}
              />
            )}
          </div>
        </div>
        <div className="order-1 flex h-12 w-full items-center gap-2 rounded-md border border-[#1696d2] bg-[#1f2937] px-3 shadow-inner ring-1 ring-[#1696d2]/20 xl:order-none xl:ml-8 xl:h-auto xl:max-w-[900px] xl:flex-1 xl:border-0 xl:bg-transparent xl:px-0 xl:shadow-none xl:ring-0">
          <IconSearch />
          <input
            ref={searchInputRef}
            type="search"
            placeholder="Search products or scan barcode"
            className="h-10 flex-1 bg-transparent border-0 outline-none text-base placeholder:text-gray-400 xl:text-[15px]"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSearchFocused(true); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (filteredProducts[0]) addToCart(filteredProducts[0]);
                else addByCodeOrBarcode();
              }
              if (e.key === 'Escape') setSearchFocused(false);
            }}
            onFocus={() => setSearchFocused(true)}
            aria-label="Search products"
          />
        </div>
        <div className="hidden ml-auto items-center xl:order-none xl:flex">
          {onLogout && (
            <POSMenu
              open={menuOpen}
              onToggle={() => setMenuOpen((o) => !o)}
              onLogout={() => { setMenuOpen(false); onLogout(); }}
              onCashInOut={() => { setMenuOpen(false); setShowCashInOut(true); }}
              onEndOfDay={() => { setMenuOpen(false); setShowEndOfDay(true); }}
              onUserInfo={() => { setMenuOpen(false); setShowUserInfo(true); }}
            />
          )}
        </div>
      </header>

      {searchFocused && search.trim() && (
        <div className="absolute top-[62px] left-2 right-2 z-50 max-h-[52dvh] overflow-y-auto border border-[#555] shadow-2xl sm:top-[116px] sm:max-h-[46dvh] xl:top-[74px] xl:left-[232px] xl:right-[405px] xl:max-h-[320px]">
          {productsLoading && products.length === 0 ? (
            <div className="h-8 bg-[#303030] px-2 py-1.5 text-sm text-gray-300">Loading products...</div>
          ) : filteredProducts.length > 0 ? (
            filteredProducts.slice(0, 8).map((p, index) => (
              <button
                key={p._id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addToCart(p);
                }}
                className={`min-h-[44px] w-full grid grid-cols-[minmax(0,1fr)_96px] items-center px-3 text-left text-sm sm:grid-cols-[minmax(0,1fr)_140px] xl:min-h-[27px] xl:px-2 ${index === 0 ? 'bg-[#1c9ed6] text-white' : 'bg-[#303030] hover:bg-[#3a3a3a] text-white'}`}
              >
                <span className="truncate">{p.name}</span>
                <span className="text-right font-bold">{formatPeso(p.price)}</span>
              </button>
            ))
          ) : (
            <div className="h-8 bg-[#303030] px-2 py-1.5 text-sm text-gray-300">No products found</div>
          )}
        </div>
      )}

      <main className="relative z-0 flex-1 flex min-h-0 flex-col xl:flex-row">
        <section className="flex min-h-0 flex-1 flex-col border-b border-[#4b4b4b] xl:min-w-0 xl:border-b-0 xl:border-r">
          <div className="h-9 grid grid-cols-[minmax(0,1fr)_48px_80px] items-center border-b border-[#1696d2] bg-[#222] text-xs font-semibold sm:grid-cols-[minmax(0,1fr)_90px_110px_120px] sm:text-sm xl:h-10 xl:grid-cols-[minmax(0,1fr)_120px_120px_130px]">
            <div className="px-2">Product name</div>
            <div className="px-2 text-center">Quantity</div>
            <div className="hidden px-2 text-right sm:block">Price</div>
            <div className="px-3 text-right">Amount</div>
          </div>
          <div className="flex-1 overflow-auto">
            {cart.items.map((item, index) => {
              const raw = (item.price || 0) * (item.qty || 0);
              const disc = (item.discountPercent ? raw * (item.discountPercent / 100) : 0) + (item.discountFixed || 0);
              const active = selectedItemId === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedItemId(item.id)}
                  className={`w-full min-h-[54px] grid grid-cols-[minmax(0,1fr)_48px_80px] items-start text-left border-b border-dashed border-[#555] text-xs hover:bg-[#303030] sm:grid-cols-[minmax(0,1fr)_90px_110px_120px] sm:text-sm xl:min-h-[47px] xl:grid-cols-[minmax(0,1fr)_120px_120px_130px] ${active ? 'bg-[#303030]' : ''}`}
                >
                  <div className="px-2 py-2 min-w-0">
                    <div className="flex gap-2">
                      <span className="text-xl leading-5 text-white">+</span>
                      <div className="min-w-0">
                        <p className="font-bold leading-5 truncate">{item.name}</p>
                        <p className="text-[11px] text-[#a9c8e8] leading-4 truncate">#{index + 1} {item.sku ? `SKU: ${item.sku}` : item.barcode ? `Barcode: ${item.barcode}` : 'SKU: -'}</p>
                        {normalizedCommentEntries(item).map((entry, commentIndex) => (
                          <p key={`${entry.text}-${commentIndex}`} className="truncate text-[11px] font-medium leading-4 text-emerald-300">
                            {entry.fromProduct ? 'Serial: ' : 'Comment: '}{entry.text}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="px-2 py-3 text-center font-bold">{item.qty}</div>
                  <div className="hidden px-2 py-3 text-right sm:block">{formatPeso(item.price)}</div>
                  <div className="px-3 py-3 text-right font-bold">{formatPeso(raw - disc)}{disc > 0 ? ' *' : ''}</div>
                </button>
              );
            })}
            {cart.items.length === 0 && (
              <div className="h-full flex items-center justify-center text-[#686868] text-lg">
                iConnectStore POS
              </div>
            )}
          </div>
          <div className="shrink-0 min-h-[72px] border-t border-[#5f5f5f] bg-[#2b2b2b] grid grid-cols-1 sm:grid-cols-[1fr_310px] xl:h-[114px]">
            <div />
            <div className="px-3 py-2 text-right xl:py-3">
              <div className="grid grid-cols-[1fr_150px] gap-y-1 text-sm xl:gap-y-2">
                <span>Subtotal</span>
                <strong>{formatPeso(subtotalBeforeCartDiscount)}</strong>
                <span className="hidden sm:block">Tax</span>
                <strong className="hidden sm:block">{formatPeso(taxAmount)}</strong>
                <span className="font-bold text-base">Total</span>
                <strong className="text-2xl leading-7 xl:text-3xl xl:leading-8">{formatPeso(total)}</strong>
              </div>
            </div>
          </div>
        </section>

        <aside className="w-full max-h-[46dvh] shrink-0 overflow-y-auto bg-[#303030] flex flex-col xl:max-h-none xl:w-[405px] xl:overflow-visible">
          <div className="grid grid-cols-4 gap-1 p-1">
            <button type="button" onClick={() => selectedItemId && removeItem(selectedItemId)} className="min-h-[52px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] text-gray-300 flex flex-col items-center justify-center gap-0.5 xl:h-[101px] xl:gap-2">
              <IconClose />
              <span className="text-xs">Delete</span>
            </button>
            <button type="button" onClick={() => { setSearchFocused(true); searchInputRef.current?.focus(); }} className="min-h-[52px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex flex-col items-center justify-center gap-0.5 xl:h-[101px] xl:gap-2">
              <span className="hidden self-start ml-2 text-sm font-bold xl:block">F3</span>
              <IconSearch />
              <span className="text-xs">Search</span>
            </button>
            <button type="button" onClick={() => (selectedItemId || cart.items[0]) && openQuantityModal(selectedItemId || cart.items[0]?.id)} className="min-h-[52px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex flex-col items-center justify-center gap-0.5 xl:h-[101px]">
              <span className="hidden self-start ml-2 text-sm font-bold xl:block">F4</span>
              <IconBasket />
              <span className="text-xs">Quantity</span>
            </button>
            <button type="button" onClick={handleNewSale} className="min-h-[52px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex flex-col items-center justify-center gap-0.5 xl:h-[101px] xl:gap-2">
              <span className="hidden self-start ml-2 text-sm font-bold xl:block">F8</span>
              <IconPlus />
              <span className="text-xs">New sale</span>
            </button>

            <button type="button" onClick={() => beginPayment('cash')} className={`h-[48px] border border-[#555] border-b-4 ${paymentMethod === 'cash' ? 'border-b-[#3f9a47]' : 'border-b-[#20a9e5]'} bg-[#242424] hover:bg-[#333] text-xs text-left px-2 xl:h-[59px]`} title="F12">
              <span className="hidden font-bold xl:block">F12</span>
              <span>Cash</span>
            </button>
            {[
              { label: 'GCash', method: 'gcash' },
              { label: 'Bank', method: 'bank' },
              { label: 'Credit card', method: 'credit_card' },
            ].map(({ label, method }) => (
              <button
                key={method}
                type="button"
                onClick={() => beginPayment(method)}
                className={`h-[48px] border border-[#555] border-b-4 ${paymentMethod === method ? 'border-b-[#3f9a47]' : 'border-b-[#20a9e5]'} bg-[#242424] hover:bg-[#333] text-xs font-semibold truncate px-2 xl:h-[59px]`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="hidden flex-1 items-center justify-center text-[#555] select-none xl:flex">
            <div className="text-center">
              <div className="mx-auto mb-3 h-24 w-28 border-[8px] border-[#4b4b4b] [transform:rotate(30deg)] flex items-center justify-center text-5xl font-bold">
                <span className="[transform:rotate(-30deg)]">iC</span>
              </div>
              <p className="text-5xl lowercase tracking-wide">iconnect</p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-1 p-1">
            <button type="button" className="hidden min-h-[64px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex-col items-center justify-center gap-1 text-xs sm:flex xl:min-h-[80px] xl:gap-2">
              <IconCart /> Cash drawer
            </button>
            <button type="button" className="hidden min-h-[64px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex-col items-center justify-center gap-1 text-xs sm:flex xl:min-h-[80px] xl:gap-2">
              <IconUser /> Dine-in
            </button>
            <button type="button" onClick={() => setShowDiscountModal(true)} className="min-h-[56px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex flex-col items-center justify-center gap-0.5 text-xs xl:min-h-[80px] xl:gap-2">
              <span className="hidden self-start ml-2 text-sm font-bold xl:block">F2</span>
              <IconPercent /> Discount
            </button>
            <button type="button" onClick={openItemCommentModal} disabled={cart.items.length === 0} className="relative min-h-[56px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] disabled:opacity-40 flex flex-col items-center justify-center gap-0.5 text-xs xl:min-h-[80px] xl:gap-2">
              <IconComment /> Comment
              {selectedCommentEntries.length > 0 ? (
                <span className="absolute right-1 top-1 min-w-5 rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-5">
                  {selectedCommentEntries.length}
                </span>
              ) : null}
            </button>
            <button type="button" onClick={() => setShowCustomerModal(true)} className="min-h-[56px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex flex-col items-center justify-center gap-0.5 text-xs xl:min-h-[80px] xl:gap-2">
              <IconUser /> Customer
            </button>
            <button type="button" className="hidden min-h-[64px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex-col items-center justify-center gap-1 text-xs sm:flex xl:min-h-[80px]">
              <IconUser /> 2615
            </button>
            <button type="button" onClick={handleSaveSale} disabled={cart.items.length === 0 || isCompleting} className="min-h-[56px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] disabled:opacity-50 flex flex-col items-center justify-center gap-0.5 font-bold xl:min-h-[80px]">
              <span className="hidden text-3xl leading-7 xl:block">F9</span>
              <IconSave />
              <span className="text-xs">Save sale</span>
            </button>
            <button type="button" className="hidden min-h-[64px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex-col items-center justify-center gap-1 text-xs sm:flex xl:min-h-[80px]">
              <IconRefund /> Refund
            </button>
            <button type="button" onClick={() => beginPayment()} disabled={cart.items.length === 0 || isCompleting} className="col-span-2 min-h-[56px] border border-[#4c9952] bg-[#438f47] hover:bg-[#4aa250] disabled:opacity-50 flex flex-col items-center justify-center font-bold xl:min-h-[80px]">
              <span className="hidden text-3xl leading-7 xl:block">F10</span>
              <span>Payment</span>
            </button>
            <button type="button" className="hidden min-h-[64px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex-col items-center justify-center gap-1 text-xs sm:flex xl:min-h-[80px]">
              <IconLock /> Lock
            </button>
            <button type="button" className="hidden min-h-[64px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] flex-col items-center justify-center gap-1 text-xs sm:flex xl:min-h-[80px]">
              <span className="self-start ml-2 text-sm font-bold">F7</span>
              <IconTransfer /> Transfer
            </button>
            <button type="button" onClick={handleNewSale} className="min-h-[56px] border border-[#9d2727] bg-[#cf2525] hover:bg-[#df2f2f] flex flex-col items-center justify-center gap-0.5 text-xs xl:min-h-[80px]">
              <IconTrash /> Void order
            </button>
            <button type="button" className="hidden min-h-[64px] border border-[#555] bg-[#303030] hover:bg-[#3a3a3a] items-center justify-center text-red-500 sm:flex xl:min-h-[80px]">
              <IconEllipsis />
            </button>
          </div>
        </aside>
      </main>

      {completeError && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-red-900/90 text-white px-4 py-3 rounded shadow-lg z-40">
          {completeError}
        </div>
      )}

      {/* Discount modal (Aronium-style: cart/item, fixed/percent) */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-sm w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">Discount</h3>
            <div className="flex gap-2 mb-3">
              <button type="button" onClick={() => setDiscountTarget('cart')} className={`flex-1 py-2 rounded text-sm ${discountTarget === 'cart' ? 'bg-indigo-600 text-white' : 'bg-gray-700'}`}>Cart discount</button>
              <button type="button" onClick={() => setDiscountTarget('item')} className={`flex-1 py-2 rounded text-sm ${discountTarget === 'item' ? 'bg-indigo-600 text-white' : 'bg-gray-700'} ${!selectedItemId ? 'opacity-50' : ''}`} disabled={!selectedItemId} title={!selectedItemId ? 'Select an item first' : ''}>Item discount</button>
            </div>
            <div className="flex gap-2 mb-3">
              <button type="button" onClick={() => setDiscountType('percent')} className={`flex-1 py-2 rounded text-sm ${discountType === 'percent' ? 'bg-indigo-600 text-white' : 'bg-gray-700'}`}>Percent</button>
              <button type="button" onClick={() => setDiscountType('fixed')} className={`flex-1 py-2 rounded text-sm ${discountType === 'fixed' ? 'bg-indigo-600 text-white' : 'bg-gray-700'}`}>Fixed</button>
            </div>
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">Discount value</label>
              {discountTarget === 'cart' ? (
                discountType === 'percent' ? (
                  <input type="number" min="0" max="100" step="1" value={discountPercent} onChange={(e) => setDiscountPercent(Number(e.target.value) || 0)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2" />
                ) : (
                  <input type="number" min="0" step="0.01" value={discountFixed} onChange={(e) => setDiscountFixed(Number(e.target.value) || 0)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2" />
                )
              ) : selectedItemId && selectedItem ? (
                discountType === 'percent' ? (
                  <input type="number" min="0" max="100" step="1" value={selectedItem.discountPercent || 0} onChange={(e) => cart.updateItemDiscount(selectedItemId, Number(e.target.value) || 0, selectedItem.discountFixed)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2" />
                ) : (
                  <input type="number" min="0" step="0.01" value={selectedItem.discountFixed || 0} onChange={(e) => cart.updateItemDiscount(selectedItemId, selectedItem.discountPercent, Number(e.target.value) || 0)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2" />
                )
              ) : (
                <p className="text-gray-500 text-sm">Select an item</p>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={clearDiscounts} className="flex-1 py-2 bg-gray-700 rounded text-sm">Clear discounts</button>
              <button type="button" onClick={() => setShowDiscountModal(false)} className="flex-1 py-2 bg-indigo-600 rounded text-sm">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Item comment / serial selector */}
      {showCommentModal && selectedItem && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-3 sm:p-5">
          <div className="flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-gray-600 bg-[#2f2f2f] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 border-b border-gray-600 px-4 py-3 sm:px-5">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold">Item comment</h3>
                <p className="truncate text-sm text-gray-300">{selectedItem.name}</p>
                <p className="text-xs text-gray-400">Quantity: {selectedItem.qty || 1}</p>
              </div>
              <button type="button" onClick={() => setShowCommentModal(false)} className="min-h-11 min-w-11 text-2xl text-gray-300 hover:text-white" aria-label="Close">×</button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {selectedCommentEntries.length > 0 ? (
                <div className="border-b border-gray-600 px-4 py-3 sm:px-5">
                  <h4 className="mb-2 text-sm font-semibold text-gray-200">Selected</h4>
                  <div className="space-y-2">
                    {selectedCommentEntries.map((entry, index) => {
                      const unavailable = entry.fromProduct && !productCommentPool.includes(entry.text);
                      return (
                        <button
                          key={`${entry.text}-${index}`}
                          type="button"
                          onClick={() => removeItemComment(index)}
                          className={`flex min-h-11 w-full items-center justify-between gap-3 border px-3 py-2 text-left ${unavailable ? 'border-red-500 bg-red-950/40' : 'border-emerald-600 bg-emerald-950/30'}`}
                        >
                          <span className="min-w-0 break-all text-sm font-medium">{entry.text}</span>
                          <span className="shrink-0 text-xs text-gray-300">{unavailable ? 'Unavailable - remove' : 'Remove'}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="px-4 py-3 sm:px-5">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-gray-200">Available serials / comments</h4>
                  <span className="text-xs text-gray-400">{availableProductComments.length} available</span>
                </div>
                {commentOptionsLoading ? (
                  <p className="border border-gray-600 px-3 py-4 text-sm text-gray-300">Loading available comments...</p>
                ) : availableProductComments.length > 0 ? (
                  <div className="max-h-64 divide-y divide-gray-600 overflow-y-auto border border-gray-600">
                    {availableProductComments.map((comment) => (
                      <button
                        key={comment}
                        type="button"
                        onClick={() => addProductComment(comment)}
                        className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left hover:bg-[#3b3b3b]"
                      >
                        <span className="h-4 w-4 shrink-0 border border-gray-400 bg-white" />
                        <span className="break-all text-sm">{comment}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="border border-gray-600 px-3 py-4 text-sm text-gray-400">No available predefined comments.</p>
                )}

                <div className="mt-4 flex gap-2">
                  <input
                    type="text"
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomComment();
                      }
                    }}
                    placeholder="Write custom comment"
                    className="min-h-11 min-w-0 flex-1 border border-gray-600 bg-[#252525] px-3 py-2 text-sm outline-none focus:border-[#25aeea]"
                  />
                  <button type="button" onClick={addCustomComment} disabled={!commentDraft.trim()} className="min-h-11 bg-[#1696d2] px-4 text-sm font-semibold disabled:opacity-40">Add</button>
                </div>
                {commentError ? <p className="mt-2 text-sm text-red-300">{commentError}</p> : null}
              </div>
            </div>

            <div className="flex justify-end border-t border-gray-600 p-3">
              <button type="button" onClick={() => setShowCommentModal(false)} className="min-h-11 bg-[#438f47] px-6 text-sm font-semibold hover:bg-[#4aa250]">Continue</button>
            </div>
          </div>
        </div>
      )}

      {/* Search modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700">
              <input
                type="search"
                placeholder="Search products by name, SKU, or barcode"
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                aria-label="Search"
              />
            </div>
            <div className="overflow-auto max-h-96 p-2">
              {productsLoading && products.length === 0 && (
                <p className="text-gray-500 py-4">Loading products...</p>
              )}
              {productsRefreshing && products.length > 0 && (
                <p className="text-gray-400 text-sm py-1" aria-live="polite">
                  Refreshing catalog...
                </p>
              )}
              {!productsLoading && filteredProducts.length === 0 && products.length > 0 && (
                <p className="text-gray-500 py-4">No products found</p>
              )}
              {!productsLoading && products.length === 0 && !productsRefreshing && (
                <p className="text-gray-500 py-4">No products loaded</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {filteredProducts.map((p) => {
                  const st = stockSummary(p, { storeName: posStoreName });
                  const stCls =
                    st.tone === 'out'
                      ? 'text-red-300'
                      : st.tone === 'low'
                        ? 'text-amber-300'
                        : st.tone === 'ok'
                          ? 'text-emerald-300'
                          : 'text-gray-400';
                  return (
                    <button
                      key={p._id}
                      type="button"
                      onClick={() => addToCart(p)}
                      className="text-left p-3 bg-gray-700 hover:bg-gray-600 rounded"
                    >
                      <p className="font-medium truncate">{p.name}</p>
                      <p className="text-sm text-indigo-300">P{Number(p.price).toFixed(2)}</p>
                      <p className={`text-[11px] font-semibold mt-1 leading-tight ${stCls}`}>{st.text}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="p-2 border-t border-gray-700 flex justify-end">
              <button type="button" onClick={() => setShowSearchModal(false)} className="px-4 py-2 bg-gray-700 rounded text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity modal */}
      {showQuantityModal && selectedItem && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-2">Quantity: {selectedItem.name}</h3>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => updateSelectedItemQuantity(Math.max(0, (selectedItem.qty || 1) - 1))} className="w-12 h-12 bg-gray-700 rounded text-xl">-</button>
              <input
                type="number"
                min="1"
                value={selectedItem.qty || 1}
                onChange={(e) => updateSelectedItemQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-24 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-center text-lg"
              />
              <button type="button" onClick={() => updateSelectedItemQuantity((selectedItem.qty || 1) + 1)} className="w-12 h-12 bg-gray-700 rounded text-xl">+</button>
            </div>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => removeItem(selectedItem.id)} className="flex-1 py-2 bg-red-600 rounded text-sm">Remove</button>
              <button type="button" onClick={() => setShowQuantityModal(false)} className="flex-1 py-2 bg-gray-600 rounded text-sm">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Customer modal */}
      {showCustomerModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">Customer</h3>
            <input
              type="text"
              placeholder="Name"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 mb-2"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
            <div className="flex gap-2 mb-2">
              <input
                type="tel"
                placeholder="Phone"
                className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
              <button type="button" onClick={handleLookupPhone} className="px-3 py-2 bg-gray-600 rounded text-sm">Look up</button>
            </div>
            <input
              type="text"
              placeholder="Address"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 mb-2"
              value={customerAddress}
              onChange={(e) => setCustomerAddress(e.target.value)}
            />
            <input
              type="email"
              placeholder="Email for invoice (optional)"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 mb-4"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
            />
            <button type="button" onClick={() => setShowCustomerModal(false)} className="w-full py-2 bg-gray-600 rounded text-sm">Done</button>
          </div>
        </div>
      )}

      {/* Payment confirmation */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3" role="dialog" aria-modal="true" aria-labelledby="payment-title">
          <div className="bg-[#303030] border border-[#555] w-full max-w-3xl max-h-[94dvh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#555] px-4 py-3">
              <div>
                <h2 id="payment-title" className="text-xl font-medium">Confirm payment</h2>
                <p className="text-sm text-gray-400">Choose payment type and verify the amount before completing the sale.</p>
              </div>
              <button type="button" onClick={() => setShowPaymentModal(false)} className="p-2 text-gray-300 hover:bg-[#444]" aria-label="Close payment">X</button>
            </div>
            <div className="grid gap-5 p-4 md:grid-cols-[260px_1fr]">
              <div>
                <h3 className="mb-2 text-sm font-medium text-gray-300">Payment type</h3>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
                  {[
                    { method: 'cash', label: 'Cash' },
                    { method: 'gcash', label: 'GCash' },
                    { method: 'bank', label: 'Bank transfer' },
                    { method: 'credit_card', label: 'Credit card' },
                  ].map(({ method, label }) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => {
                        setPaymentMethod(method);
                        setPaymentReference('');
                        setPaymentError('');
                        setCashReceived(Number(total).toFixed(2));
                      }}
                      className={`min-h-[52px] border px-4 text-left text-sm ${paymentMethod === method ? 'border-[#20a9e5] bg-[#17658a]' : 'border-[#555] bg-[#262626] hover:bg-[#3b3b3b]'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <div className="border-b border-[#555] pb-4">
                  <div className="flex items-center justify-between text-gray-300"><span>Total</span><strong className="text-3xl text-[#20a9e5]">P{formatPeso(total)}</strong></div>
                </div>
                {paymentMethod === 'cash' ? (
                  <div>
                    <label className="mb-1 block text-sm text-gray-300">Cash received</label>
                    <input autoFocus type="number" min="0" step="0.01" value={cashReceived} onChange={(e) => { setCashReceived(e.target.value); setPaymentError(''); }} className="w-full border border-[#20a9e5] bg-[#242424] px-4 py-3 text-right text-2xl" />
                    <div className="mt-3 flex items-center justify-between border-t border-[#555] pt-3">
                      <span className="text-lg">Change</span>
                      <strong className={`text-3xl ${Number(cashReceived) >= total ? 'text-green-400' : 'text-red-400'}`}>P{formatPeso(Math.max(0, Number(cashReceived) - total))}</strong>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="mb-1 block text-sm text-gray-300">Transaction reference number</label>
                    <input autoFocus type="text" value={paymentReference} onChange={(e) => { setPaymentReference(e.target.value); setPaymentError(''); }} placeholder="Required reference number" className="w-full border border-[#20a9e5] bg-[#242424] px-4 py-3 text-lg" />
                    <p className="mt-2 text-xs text-gray-400">Enter the GCash, bank, or credit-card approval/reference number.</p>
                  </div>
                )}
                {paymentMethod === 'cash' && (
                  <div>
                    <label className="mb-1 block text-sm text-gray-400">Cash transaction reference (optional)</label>
                    <input type="text" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} placeholder="Optional" className="w-full border border-[#555] bg-[#242424] px-3 py-2" />
                  </div>
                )}
                {paymentError && <p className="text-sm text-red-400">{paymentError}</p>}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <button type="button" onClick={() => setShowPaymentModal(false)} className="min-h-[48px] border border-[#666] bg-[#444] hover:bg-[#555]">Cancel</button>
                  <button type="button" onClick={handlePayment} disabled={isCompleting} className="min-h-[48px] bg-[#438f47] font-bold hover:bg-[#4aa250] disabled:opacity-50">{isCompleting ? 'Saving...' : 'Confirm payment'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cash In / Out modal (Aronium-style) */}
      {showCashInOut && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">Cash In / Out</h3>
            <p className="text-sm text-gray-400 mb-4">Add or remove cash from the register. Open entries are closed on day close.</p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Add cash (amount, description)</label>
                <div className="flex gap-2">
                  <input type="number" min="0" step="0.01" placeholder="Amount" className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2" />
                  <input type="text" placeholder="Description" className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2" />
                </div>
                <button type="button" className="mt-2 px-4 py-2 bg-green-600 rounded text-sm">Add cash</button>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Remove cash (amount, reason)</label>
                <div className="flex gap-2">
                  <input type="number" min="0" step="0.01" placeholder="Amount" className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2" />
                  <input type="text" placeholder="Reason" className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2" />
                </div>
                <button type="button" className="mt-2 px-4 py-2 bg-red-600 rounded text-sm">Remove cash</button>
              </div>
            </div>
            <button type="button" onClick={() => setShowCashInOut(false)} className="w-full py-2 bg-gray-600 rounded text-sm">Close</button>
          </div>
        </div>
      )}

      {/* End of day (Z Report) modal (Aronium-style) */}
      {showEndOfDay && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-lg w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">End of day (Z Report)</h3>
            {cart.items.length > 0 && (
              <p className="text-amber-400 text-sm mb-4">Close all open sales before proceeding. You have {cart.items.length} item(s) in current sale.</p>
            )}
            <div className="space-y-2 mb-4">
              <button type="button" className="w-full py-3 bg-gray-700 hover:bg-gray-600 rounded text-left px-4">Cash out (current user)</button>
              <button type="button" className="w-full py-3 bg-gray-700 hover:bg-gray-600 rounded text-left px-4">Cash out all users</button>
              <button type="button" className="w-full py-3 bg-gray-700 hover:bg-gray-600 rounded text-left px-4">Close register</button>
            </div>
            <p className="text-xs text-gray-500 mb-4">Cash in and cash out totals will be printed on the Z report.</p>
            <button type="button" onClick={() => setShowEndOfDay(false)} className="w-full py-2 bg-gray-600 rounded text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* User info modal (Aronium-style) */}
      {showUserInfo && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">User info</h3>
            <p className="text-sm text-gray-400 mb-4">Change user details and password. Leave password blank to keep unchanged.</p>
            <div className="space-y-2 mb-4">
              <input type="text" placeholder="First name" defaultValue={typeof window !== 'undefined' && sessionStorage?.getItem?.('pos_username')} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2" />
              <input type="password" placeholder="Password (leave blank to keep)" className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2" />
              <button type="button" className="w-full py-2 bg-indigo-600 rounded text-sm">Change profile or password</button>
            </div>
            <button type="button" onClick={() => setShowUserInfo(false)} className="w-full py-2 bg-gray-600 rounded text-sm">Close</button>
          </div>
        </div>
      )}

      {/* Receipt complete modal */}
      {lastOrder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" role="dialog" aria-labelledby="receipt-title">
          <div className="bg-[#303030] border border-[#555] max-w-4xl w-full shadow-xl">
            <div className="border-b border-[#555] p-5 text-center">
              <p className="text-lg">Change: <strong className="text-3xl text-green-400">P{formatPeso(lastOrder.changeAmount)}</strong></p>
              <h2 id="receipt-title" className="mt-3 text-3xl font-light">How would the customer like their receipt?</h2>
              <p className="mt-2 text-sm text-gray-400">Document {lastOrder.documentNumber || lastOrder.orderId} | Total P{formatPeso(lastOrder.totalAmount)}</p>
              {lastOrder.paymentReference && <p className="text-sm text-gray-400">Reference: {lastOrder.paymentReference}</p>}
            </div>
            <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
              <button type="button" onClick={handlePrintReceipt} className="min-h-[108px] border border-[#20a9e5] bg-[#333] hover:bg-[#3d3d3d] text-lg">Print receipt</button>
              <button type="button" onClick={openA4Invoice} className="min-h-[108px] border border-[#666] bg-[#333] hover:bg-[#3d3d3d] text-lg">Print A4 invoice</button>
              <button type="button" onClick={handleSendEmail} className="min-h-[108px] border border-[#666] bg-[#333] hover:bg-[#3d3d3d] text-lg">Send email</button>
              <button type="button" onClick={openA4Invoice} className="min-h-[108px] border border-[#666] bg-[#333] hover:bg-[#3d3d3d] text-lg">Save as PDF</button>
            </div>
            <div className="flex justify-end border-t border-[#555] p-4">
              <button type="button" onClick={() => { setLastOrder(null); setCashReceived(''); setPaymentReference(''); }} className="min-w-[150px] bg-[#438f47] px-6 py-3 font-bold hover:bg-[#4aa250]">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
